/**
 * 加密备份（.zxbak）的公开契约与数据形状。
 *
 * 容器层与业务层解耦：本文件只描述「manifest / 摘要 / 恢复结果 / 依赖注入」，
 * 具体加密、ZIP 与 IndexedDB 读写分别在 `container.ts` / `archive.ts` / `restore.ts`。
 */
import type { ApiUser, LedgerEntry, WalletSnapshot } from '../../core/api';
import type { MimoSnapshot } from '../mimo/storage';
import type { VoicePreferences, TtsMode } from '../mimo/contracts';

export const BACKUP_MANIFEST_FORMAT = 'zxtalk.backup.manifest' as const;
export const BACKUP_FORMAT_VERSION = 2;
export const BACKUP_EXTENSION = '.zxbak';
/** 明文容器魔数：`ZXTALKBK`。 */
export const BACKUP_MAGIC = 'ZXTALKBK';
export const PBKDF2_ITERATIONS = 600_000;
export const SALT_BYTES = 16;
export const IV_BYTES = 12;
export const MAX_ARCHIVE_BYTES = 1024 * 1024 * 1024;
export const MAX_ARCHIVE_ENTRY_BYTES = 256 * 1024 * 1024;
export const MAX_ARCHIVE_ENTRIES = 50_000;

export interface BackupMediaEntry {
  sha256: string;
  mime: string;
  bytes: number;
  ext: string;
}

export interface BackupCounts {
  conversations: number;
  messages: number;
  presets: number;
  providers: number;
  memories: number;
  gallery: number;
  media: number;
  localStorageKeys: number;
  ledger: number;
  ttsAudio: number;
  ttsReferences: number;
  moments: number;
  automationEvents: number;
}

export interface BackupLedgerInfo {
  included: boolean;
  count: number;
  capturedAt: number | null;
  warning?: string;
}

export interface BackupManifest {
  format: typeof BACKUP_MANIFEST_FORMAT;
  version: number;
  /** 同一份备份的稳定 id，用于本地流水快照幂等写入。 */
  backupId: string;
  exportedAt: number;
  account: { id: string; email: string };
  counts: BackupCounts;
  media: { count: number; bytes: number; entries: BackupMediaEntry[] };
  ledger: BackupLedgerInfo;
  warnings: string[];
}

export interface BackupSummary {
  backupId: string;
  exportedAt: number;
  accountId: string;
  accountEmail: string;
  counts: BackupCounts;
  mediaBytes: number;
  ledger: BackupLedgerInfo;
  warnings: string[];
}

export interface BackupInspection {
  summary: BackupSummary;
  manifest: BackupManifest;
}

export interface EncryptedBackup {
  blob: Blob;
  filename: string;
  summary: BackupSummary;
  manifest: BackupManifest;
}

export type RestoreMode = 'merge' | 'replace';

export interface RestoreCounts {
  conversations: number;
  messages: number;
  presets: number;
  providers: number;
  memories: number;
  gallery: number;
  media: number;
  ledger: number;
  ttsAudio: number;
  moments: number;
  automationEvents: number;
}

export interface RestoreResult {
  mode: RestoreMode;
  counts: RestoreCounts;
  warnings: string[];
  ttsPreferences: 'restored' | 'failed' | 'skipped';
  safetyBackup?: { filename: string; blob: Blob };
}

/** 备份文件里的本地存储快照：只保留应用自己管理的键。 */
export type BackupLocalStorage = Record<string, string>;

export interface BackupMimoAudioRecord {
  key: string;
  id: string;
  sha256: string;
  bytes: number;
  mime: string;
  links: string[];
  preview: boolean;
  createdAt: number;
  mediaRef: string;
}

export interface BackupMimoReferenceRecord {
  name: string;
  sha256: string;
  consent: true;
  mime: string;
  mediaRef: string;
}

export interface BackupMimoModeRecord {
  scope: string;
  mode: TtsMode;
}

export interface BackupMimoFile {
  serverPreferences?: VoicePreferences;
  audio: BackupMimoAudioRecord[];
  reference?: BackupMimoReferenceRecord;
  modes: BackupMimoModeRecord[];
}

export interface BackupAccountHistory {
  capturedAt: number | null;
  included: boolean;
  warning?: string;
  wallet: WalletSnapshot | null;
  entries: LedgerEntry[];
}

export interface BackupAccountRecord {
  id: string;
  email: string;
}

export interface BackupLedgerPage {
  entries: LedgerEntry[];
  hasMore: boolean;
}

/** 只读账户接口；测试可注入 fake，生产默认走 apiClient。 */
export interface BackupApi {
  currentUser(): Promise<ApiUser>;
  wallet(): Promise<WalletSnapshot>;
  ledgerPage(offset?: number, limit?: number): Promise<BackupLedgerPage>;
}

/** 服务端 TTS 偏好接口；不可用时导出/导入只给警告，不阻断本地数据。 */
export interface BackupTtsApi {
  account(): Promise<{ preferences: VoicePreferences }>;
  savePreferences(preferences: VoicePreferences): Promise<unknown>;
}

/** Mimo 设备库快照接口；默认实现是 mimoStorage。 */
export interface BackupMimoApi {
  exportSnapshot(userId: string): Promise<MimoSnapshot>;
  restoreSnapshot(userId: string, snapshot: MimoSnapshot, mode: RestoreMode): Promise<void>;
  clearUserData(userId: string): Promise<void>;
}

export interface CreateBackupOptions {
  /** 已登录账户；缺省时调用 api.currentUser()。 */
  account?: BackupAccountRecord;
  api?: BackupApi;
  tts?: BackupTtsApi;
  mimo?: BackupMimoApi;
  now?: number;
  randomBytes?: (length: number) => Uint8Array;
}

export interface RestoreBackupOptions {
  userId: string;
  email?: string;
  mode: RestoreMode;
  api?: BackupApi;
  tts?: BackupTtsApi;
  mimo?: BackupMimoApi;
  /** 完整替换前自动下载安全备份；缺省时由调用方用返回值自行下载。 */
  downloadSafetyBackup?: (filename: string, blob: Blob) => void;
  onProgress?: (stage: string) => void;
  now?: number;
  randomBytes?: (length: number) => Uint8Array;
}

export type BackupErrorCode =
  | 'LOGIN_REQUIRED'
  | 'PASSWORD'
  | 'FORMAT'
  | 'LIMIT'
  | 'ACCOUNT_MISMATCH'
  | 'VALIDATION'
  | 'RESTORE';

export class BackupError extends Error {
  readonly code: BackupErrorCode;

  constructor(code: BackupErrorCode, message: string) {
    super(message);
    this.name = 'BackupError';
    this.code = code;
  }
}
